import { AccountType } from "../types";
import { APIClientServices } from "./APIClientServices";
import { debounce, EventEmitter } from "@ismael1361/utils";
import { HandleError, sanitizeEmailPrefix, Validate } from "../utils";
import { AccountModel, IAccountModel } from "shared/model";

let currentUser: AccountModel | null = null;
let currentTokenExpiration: NodeJS.Timeout | null = null;
let currentUserReferenceCode: AccountType.ReferenceCodeType | null = null;
let byReferenceCode: string | null = null;
let persistSession: boolean = false;

const emitter = new EventEmitter<{
	userChanged: [user: AccountModel | null];
	error: [error: HandleError];
}>();

emitter.prepared = true;

const emitUserChanged = debounce((user: AccountModel | null) => {
	emitter.emit("userChanged", user);
}, 200);

const TOKEN_ID = "__auth_token__";
const PERSISTENT_DAYS = 30;

// ─── Helpers de cookie ────────────────────────────────────────────────────────
function setCookie(name: string, value: string, days: number) {
	const maxAge = days * 24 * 60 * 60;
	const secure = window.location.protocol === "https:" ? "; Secure" : "";
	document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${maxAge}; SameSite=Lax${secure}; Path=/`;
}

function getCookie(name: string): string | null {
	const match = document.cookie.split("; ").find((row) => row.startsWith(`${name}=`));
	return match ? decodeURIComponent(match.split("=").slice(1).join("=")) : null;
}

function deleteCookie(name: string) {
	document.cookie = `${name}=; Max-Age=0; SameSite=Lax; Path=/`;
}

export class UserServices {
	static set expiration(expiration: number | null) {
		if (currentTokenExpiration) {
			clearTimeout(currentTokenExpiration);
		}

		if (typeof expiration !== "number") {
			return;
		}

		currentTokenExpiration = setTimeout(() => {
			currentUser = null;
			this.getCurrentUser();
		}, expiration - Date.now());
	}

	static get currentToken(): string | null {
		// Ordem: sessionStorage → cookie → localStorage (compatibilidade retroativa)
		return sessionStorage.getItem(TOKEN_ID) ?? getCookie(TOKEN_ID) ?? localStorage.getItem(TOKEN_ID);
	}

	static set currentToken(token: string | null) {
		if (token) {
			if (persistSession) {
				// Login persistente: armazena em cookie com validade de 30 dias
				setCookie(TOKEN_ID, token, PERSISTENT_DAYS);
				sessionStorage.removeItem(TOKEN_ID);
			} else {
				// Login de sessão: armazena em sessionStorage (limpo ao fechar browser)
				sessionStorage.setItem(TOKEN_ID, token);
				deleteCookie(TOKEN_ID);
			}
			// Remove token legado do localStorage em ambos os casos
			localStorage.removeItem(TOKEN_ID);
		} else {
			sessionStorage.removeItem(TOKEN_ID);
			deleteCookie(TOKEN_ID);
			localStorage.removeItem(TOKEN_ID);
			this.expiration = null;
		}
		emitUserChanged(this.currentUser);
	}

	/**
	 * Armazena o token de acesso com controle de persistência.
	 * @param token O token de acesso (ou null para limpar).
	 * @param persistent Se true, o token persiste por 30 dias (cookie). Se false, dura apenas a sessão (sessionStorage).
	 */
	static setToken(token: string | null, persistent: boolean) {
		persistSession = persistent;
		this.currentToken = token;
	}

	static get currentUser(): AccountModel | null {
		return currentUser?.clone() ?? null;
	}

	static set currentUser(user: AccountModel | null) {
		if (currentUser && user) {
			currentUser.clear(user.toJSON());
		} else if (!currentUser && user) {
			currentUser = user.clone();
		} else {
			currentUser = null;
		}
		emitUserChanged(currentUser);
	}

	static get currentUserReferenceCode(): AccountType.ReferenceCodeType | null {
		return currentUserReferenceCode;
	}

	static async applyReferenceCode(referenceCode: string) {
		byReferenceCode = referenceCode;

		if (this.currentUser) {
			return;
		}
	}

	/**
	 * Obtém o usuário atual após carregar o token do usuário.
	 * @returns O usuário atual.
	 * @example
	 * ```ts
	 * await UserServices.ready((user)=>{
	 *  console.log(user);
	 * });
	 * ```
	 */
	static async ready<R>(callback: (user: AccountModel | null) => R | Promise<R>): Promise<R> {
		const user = await this.getCurrentUser();
		return await Promise.race([callback(user)]);
	}

	/**
	 * Obtém o usuário atual.
	 * @returns O usuário atual.
	 * @throws {Error} Se o usuário não estiver logado.
	 * @example
	 * ```ts
	 * const user = await UserServices.getCurrentUser();
	 * console.log(user);
	 * ```
	 */
	static async getCurrentUser(): Promise<AccountModel | null> {
		if (!emitter.prepared) {
			return new Promise((resolve) => {
				emitter.on("userChanged", (user) => {
					resolve(user);
				});
			});
		}

		emitter.prepared = false;

		if (this.currentUser) {
			emitter.prepared = true;
			return this.currentUser;
		}

		if (!this.currentToken) {
			emitter.prepared = true;
			this.currentUser = null;
			return null;
		}

		try {
			const response = await APIClientServices.get<AccountType.AccountResponseType>("v1/auth/account", {
				referrer_code: byReferenceCode,
			}).catch(() => {
				return APIClientServices.post<AccountType.AccountResponseType>("v1/auth/refresh-token", {
					token: byReferenceCode,
				});
			});

			currentUserReferenceCode = response.referenceCode;
			this.currentUser = AccountModel.parse(response.user);
			this.currentToken = response.access_token;
			this.expiration = response.expiration;

			emitter.prepared = true;
		} catch (error: any) {
			const errorCode = error?.code || error?.status;

			console.error(errorCode);

			// Se for erro 401 (não autorizado) ou 400 (token inválido/corrompido), tentar refresh token
			if (errorCode === 401 || errorCode === 400) {
				emitter.prepared = true;
				this.currentUser = null;
				this.currentToken = null;
				emitter.emit("error", new HandleError("User is not authorized", "USER-UNAUTHORIZED", 401));
				return null;
			}
			// Para outros erros (rede, servidor 500, etc.), manter o token e retornar null temporariamente
			emitter.prepared = true;
			emitter.emit("error", new HandleError("Failed to fetch user data", "USER-FETCH-ERROR", errorCode));
		}

		return this.currentUser;
	}

	static async getAccount() {
		const response = await APIClientServices.get<AccountType.AccountResponseType>("v1/auth/account", {
			referrer_code: byReferenceCode,
		});

		currentUserReferenceCode = response.referenceCode;
		this.currentUser = AccountModel.parse(response.user);
		this.currentToken = response.access_token;
		this.expiration = response.expiration;
	}

	/**
	 * Cria um novo usuário.
	 * @param user Os dados do usuário a serem criados.
	 * @param signed Se o usuário deve ser logado após a criação.
	 * @returns O usuário criado.
	 * @throws {Error} Se o usuário já existir.
	 * @throws {Error} Se o usuário não for válido.
	 *
	 * @example
	 * ```ts
	 * await UserServices.createUser({
	 *  email: "test@example.com",
	 *  password: "password",
	 *  displayName: "Test User",
	 * });
	 * ```
	 */
	static async createUser(user: AccountType.NewAccountDetailsType, signed: boolean = false): Promise<AccountModel> {
		user.username = user.username ? user.username : sanitizeEmailPrefix(user.email);

		const response = await APIClientServices.post<AccountType.AccountResponseType>(
			"v1/auth/signup",
			{
				username: user.username,
				email: user.email,
				password: user.password,
				displayName: user.displayName,
				settings: user.settings,
			},
			{
				referrer_code: byReferenceCode,
			},
		);

		if (signed) {
			currentUserReferenceCode = response.referenceCode;
			this.currentUser = AccountModel.parse(response.user);
			this.currentToken = response.access_token;
			this.expiration = response.expiration;
			return this.currentUser;
		}

		return AccountModel.parse(response.user);
	}

	/**
	 * Atualiza o usuário atual.
	 * @param user Os dados do usuário a serem atualizados.
	 * @returns O usuário atualizado.
	 *
	 * @example
	 * ```ts
	 * await UserServices.updateUser({
	 *  displayName: "Test User",
	 * });
	 * ```
	 */
	static async updateUser(user: AccountType.UpdateAccountDetailsType): Promise<AccountModel> {
		const response = await APIClientServices.put<AccountType.AccountResponseType>(
			"v1/auth/account",
			{
				username: user.username,
				displayName: user.displayName,
				settings: user.settings,
			},
			{
				referrer_code: byReferenceCode,
			},
		);

		currentUserReferenceCode = response.referenceCode;
		this.currentUser = AccountModel.parse(response.user);
		this.currentToken = response.access_token;
		this.expiration = response.expiration;

		return this.currentUser;
	}

	/**
	 * Faz upload de uma foto de avatar do usuário (POST /v1/auth/avatar).
	 *
	 * O backend valida mime (png/jpeg/jpg/webp), tamanho máx 5MB, salva no
	 * StorageService (FTP ou disco) como `avatars/{user.uid}.{ext}` e
	 * atualiza `settings.avatarUrl` no banco. O usuário em memória também
	 * é atualizado para refletir a nova URL sem precisar recarregar.
	 *
	 * @param file - Arquivo de imagem selecionado pelo usuário.
	 * @returns A URL pública do avatar.
	 */
	static async uploadAvatar(file: File): Promise<string> {
		const formData = new FormData();
		formData.append("file", file);
		const response = await APIClientServices.post<{ message: string; url: string }>(
			"v1/auth/avatar",
			formData,
		);

		// Sincroniza o user local com a nova URL via updateUser (re-parsea o model
		// e dispara o evento "update", garantindo re-render nos componentes que
		// consomem via useAccount).
		const currentSettings = (this.currentUser?.settings ?? {}) as Record<string, any>;
		await this.updateUser({
			settings: {
				...currentSettings,
				avatarUrl: response.url,
				profilePictureUrl: null, // limpa o legado p/ não ofuscar o novo valor
			} as any,
		});

		return response.url;
	}

	/**
	 * Remove a foto do avatar do usuário (zera `settings.avatarUrl`).
	 * O arquivo no storage não é deletado — pode ser limpo por job de retenção.
	 * O fallback gradient + iniciais da marca volta a ser exibido pelo UserAvatar.
	 */
	static async removeAvatar(): Promise<AccountModel> {
		const currentSettings = (this.currentUser?.settings ?? {}) as Record<string, any>;
		return await this.updateUser({
			settings: {
				...currentSettings,
				avatarUrl: null,
				profilePictureUrl: null,
			} as any,
		});
	}

	/**
	 * Faz o login do usuário com o email e senha fornecidos.
	 * @param email O email do usuário.
	 * @param password A senha do usuário.
	 * @param code O código de 2FA, se necessário.
	 * @returns O usuário logado.
	 * @throws {Error} Se o email ou a senha estiverem incorretos.
	 * @throws {Error} Se o usuário não for encontrado.
	 *
	 * @example
	 * ```ts
	 * const user = await UserServices.signInWithEmailAndPassword("email@example.com", "password", "123456");
	 * console.log(user);
	 * ```
	 */
	static async signInWithEmailAndPassword(
		email: string,
		password: string,
		code?: string,
		rememberMe: boolean = false,
		pin?: string,
	): Promise<AccountModel> {
		const response = await APIClientServices.post<
			AccountType.AccountResponseType | AccountType.SignInRequires2FAResponseType | AccountType.SignInRequiresPinResponseType
		>(
			"v1/auth/signin",
			{
				method: "email",
				email,
				password,
				code,
				pin,
				rememberMe,
			},
			{
				referrer_code: byReferenceCode,
			},
		);

		if ("requires2FA" in response) {
			throw new HandleError(`2FA required: ${response.message}`, "2FA_REQUIRED", 401);
		}

		if ("requiresPin" in response) {
			throw new HandleError(`PIN required: ${response.message}`, "PIN_REQUIRED", 401);
		}

		currentUserReferenceCode = response.referenceCode;
		this.currentUser = AccountModel.parse(response.user);
		this.setToken(response.access_token, rememberMe);
		this.expiration = response.expiration;

		return this.currentUser;
	}

	/**
	 * Faz o login do usuário com o username e senha fornecidos.
	 * @param username O username do usuário.
	 * @param password A senha do usuário.
	 * @param code O código de 2FA, se necessário.
	 * @returns O usuário logado.
	 * @throws {Error} Se o usuário não for encontrado.
	 * @throws {Error} Se a senha estiver incorreta.
	 * @throws {Error} Se o usuário não estiver ativo.
	 * @throws {Error} Se o usuário estiver bloqueado.
	 *
	 * @example
	 * ```ts
	 * const user = await AuthController.signInWithUsernameAndPassword("username", "password", "123456");
	 * console.log(user);
	 * ```
	 */
	static async signInWithUsernameAndPassword(
		username: string,
		password: string,
		code?: string,
		rememberMe: boolean = false,
		pin?: string,
	): Promise<AccountModel> {
		const response = await APIClientServices.post<
			AccountType.AccountResponseType | AccountType.SignInRequires2FAResponseType | AccountType.SignInRequiresPinResponseType
		>(
			"v1/auth/signin",
			{
				method: "account",
				username,
				password,
				code,
				pin,
				rememberMe,
			},
			{
				referrer_code: byReferenceCode,
			},
		);

		if ("requires2FA" in response) {
			throw new HandleError(`2FA required: ${response.message}`, "2FA_REQUIRED", 401);
		}

		if ("requiresPin" in response) {
			throw new HandleError(`PIN required: ${response.message}`, "PIN_REQUIRED", 401);
		}

		currentUserReferenceCode = response.referenceCode;
		this.currentUser = AccountModel.parse(response.user);
		this.setToken(response.access_token, rememberMe);
		this.expiration = response.expiration;

		return this.currentUser;
	}

	/**
	 * Faz o login do usuário com o email/username e senha fornecidos.
	 * @param account O email ou username do usuário.
	 * @param password A senha do usuário.
	 * @param code O código de 2FA, se necessário.
	 * @returns Retorna uma Promise que resolve com o usuário logado.
	 * @example
	 * ```ts
	 * await AuthController.signInWithEmailAndPassword("email@example.com", "password");
	 * ```
	 */
	static signInWithAccountAndPassword(
		account: string,
		password: string,
		code?: string,
		rememberMe: boolean = false,
		pin?: string,
	): Promise<AccountModel> {
		if (Validate.isValidEmail(account)) {
			return this.signInWithEmailAndPassword(account, password, code, rememberMe, pin);
		}

		return this.signInWithUsernameAndPassword(account, password, code, rememberMe, pin);
	}

	/**
	 * Faz o login do usuário com conta de um provedor OAuth2.
	 * @param providerName O nome do provedor OAuth2.
	 * @param callbackUrl A URL de retorno.
	 * @param options As opções do provedor OAuth2.
	 * @returns Retorna uma Promise que resolve com o usuário logado.
	 *
	 * @example
	 * ```ts
	 * const callbackUrl = window.location.origin + '/authenticated';
	 * UserServices.startAuthProviderSignIn('google', callbackUrl)
	 * .then(redirectUrl => {
	 *     window.location.href = redirectUrl; // Send user to auth provider's login screen
	 * });
	 * ```
	 */
	static async startAuthProviderSignIn(providerName: string, callbackUrl: string, options: Record<PropertyKey, any> = {}) {
		const optionParams = Object.keys(options).reduce(
			(current, key) => {
				current[`option_${key}`] = options[key];
				return current;
			},
			{} as Record<string, any>,
		);
		const result = await APIClientServices.get<{ redirectUrl: string }>("v1/oauth2/init", {
			provider: providerName,
			callbackUrl,
			...optionParams,
		});
		return result.redirectUrl;
	}

	/**
	 * Finaliza o login do usuário com conta de um provedor OAuth2.
	 * @param callbackResult O resultado do callback do provedor OAuth2.
	 * @returns Retorna uma Promise que resolve com o usuário logado.
	 *
	 * @example
	 * ```ts
	 * const callbackResult = window.location.search.match(/[?&]result=(.*?)(?:&|$)/)[1]; // Or some other way you'd get the ?result from the url
	 * UserServices.finishAuthProviderSignIn(callbackResult)
	 * .then(result => {
	 *     console.log(`User ${result.user.email} signed in with ${result.provider.name}`);
	 * });
	 * ```
	 */
	static async finishAuthProviderSignIn(callbackResult: string) {
		let result: {
			provider: { name: string; access_token: string; refresh_token: string; expires_in: number };
			access_token: string;
			expiration: number;
			user: IAccountModel;
		};
		try {
			result = JSON.parse(atob(callbackResult));
		} catch (err) {
			throw new Error(`Invalid result`);
		}

		// this.currentUser = result.user;
		this.currentToken = result.access_token;
		this.expiration = result.expiration;
		const user = await this.getCurrentUser();

		return { ...result, user };
	}

	/**
	 * Atualizar o token de acesso do usuário atual.
	 * @param providerName O nome do provedor OAuth2.
	 * @param refreshToken O token de atualização do provedor OAuth2.
	 * @returns Retorna uma Promise que resolve com o usuário logado.
	 *
	 * @example
	 * ```ts
	 * const keepAlive = (provider) => {
	 *     // Schedule token refresh 1 minute before it expires
	 *     const refreshMs = (provider.expires_in - 60) * 1000;
	 *     setTimeout(() => {
	 *         UserServices.refreshAuthProviderToken(provider.name, provider.refresh_token)
	 *         .then(result => {
	 *             keepAlive(result.provider); // Schedule again
	 *         })
	 *     }, refreshMs);
	 * };
	 *
	 * UserServices.finishAuthProviderSignIn(callbackResult)
	 * .then(result => {
	 *     // schedule a token refresh:
	 *     keepAlive(result.provider);
	 * });
	 * ```
	 */
	static async refreshAuthProviderToken(providerName: string, refreshToken: string) {
		const result = await APIClientServices.get<{
			provider: { name: string; access_token: string; refresh_token: string; expires_in: number };
		}>("v1/oauth2/refresh", { provider: providerName, refresh_token: refreshToken });
		const user = await this.getCurrentUser();
		return { ...result, access_token: this.currentToken, expiration: this.expiration, user };
	}

	/**
	 * Faz o logout do usuário atual.
	 * @param everywhere Se verdadeiro, o logout será feito em todos os dispositivos.
	 * @returns Retorna uma Promise que resolve quando o logout é concluído.
	 * @example
	 * ```ts
	 * await AuthController.signOut();
	 * ```
	 */
	static async signOut(everywhere: boolean = false): Promise<void> {
		if (!this.currentUser) {
			return;
		}

		APIClientServices.post("v1/auth/signout", { everywhere }, { token: this.currentToken ?? "" });

		this.currentUser = null;
		this.currentToken = null;
	}

	/**
	 * Faz o logout do usuário atual.
	 * @param everywhere Se verdadeiro, o logout será feito em todos os dispositivos.
	 * @returns Retorna uma Promise que resolve quando o logout é concluído.
	 * @example
	 * ```ts
	 * await AuthController.logout();
	 * ```
	 */
	static async logout(everywhere: boolean = false): Promise<void> {
		return await this.signOut(everywhere);
	}

	/**
	 * Evento que se dispara quando o estado de autenticação é alterado.
	 * @param callback Função a ser chamada quando o estado de autenticação é alterado.
	 * @returns Retorna um objeto de cancelamento do evento.
	 * @example
	 * ```ts
	 * const cancel = AuthController.onAuthStateChanged((user) => {
	 *  console.log(user);
	 * });
	 *
	 * // cancela o evento
	 * cancel.stop();
	 * ```
	 */
	static onAuthStateChanged(callback: (user: AccountModel | null) => void) {
		return emitter.on("userChanged", callback);
	}

	/**
	 * Evento que se dispara quando o token de autenticação é alterado.
	 * @param callback Função a ser chamada quando o token é alterado.
	 * @returns Retorna um objeto de cancelamento do evento.
	 * @example
	 * ```ts
	 * const cancel = AuthController.onTokenChanged((token) => {
	 *  console.log(token);
	 * });
	 *
	 * // cancela o evento
	 * cancel.stop();
	 * ```
	 */
	static onIdTokenChanged(callback: (user: string | null) => void) {
		return this.onAuthStateChanged(() => {
			callback(this.currentToken);
		});
	}

	/**
	 * Evento que se dispara quando ocorre um erro relacionado ao usuário.
	 * @param callback Função a ser chamada quando um erro ocorre.
	 * @return Retorna um objeto de cancelamento do evento.
	 * @example
	 * ```ts
	 * const cancel = UserServices.onError((error) => {
	 *  console.error(error);
	 * });
	 * ```
	 */
	static onError(callback: (error: HandleError) => void) {
		return emitter.on("error", callback);
	}

	/**
	 * Enviar código de verificação para o email do usuário.
	 * @param email O email do usuário.
	 * @returns Retorna uma Promise que resolve quando o email é verificado.
	 *
	 * @example
	 * ```ts
	 * await AuthController.verifyEmail("email@example.com");
	 * ```
	 */
	static async verifyEmail(email: string): Promise<void> {
		await APIClientServices.post("v1/auth/verify-email", { email });
	}

	/**
	 * Confirmar código de verificação para o email do usuário.
	 * @param email O email do usuário.
	 * @param code O código de verificação.
	 * @returns Retorna uma Promise que resolve quando o email é verificado.
	 *
	 * @example
	 * ```ts
	 * await AuthController.confirmEmail("email@example.com", "123456");
	 * ```
	 */
	static async confirmEmail(email: string, code: string): Promise<void> {
		await APIClientServices.post("v1/auth/verify-email", { email, code });
	}

	/**
	 * Verificar se o email e/ou nome de usuário já existe.
	 * @param email O email do usuário.
	 * @param username O nome de usuário do usuário (opcional).
	 * @returns Retorna uma Promise que resolve com um objeto contendo as informações de existência.
	 *
	 * @example
	 * ```ts
	 * const exists = await AuthController.checkExistence("email@example.com", "username");
	 * console.log(exists);
	 * ```
	 */
	static async checkExistence(email: string, username: string = "") {
		return await APIClientServices.post<{
			email: boolean;
			username: boolean;
		}>("v1/auth/check-existence", { email, username });
	}

	/**
	 * Enviar email de recuperação de senha para o email do usuário.
	 * @param email O email do usuário.
	 * @returns Retorna uma Promise que resolve quando o email é enviado.
	 *
	 * @example
	 * ```ts
	 * await AuthController.forgotPassword("email@example.com");
	 * ```
	 */
	static async forgotPassword(email: string): Promise<void> {
		await APIClientServices.post("v1/auth/forgot-password", { email });
	}

	/**
	 * Resetar senha do usuário.
	 * @param email O email do usuário.
	 * @param code O código de recuperação de senha.
	 * @param newPassword A nova senha do usuário.
	 * @returns Retorna uma Promise que resolve quando a senha é resetada.
	 *
	 * @example
	 * ```ts
	 * await AuthController.resetPassword("email@example.com", "123456", "newpassword");
	 * ```
	 */
	static async resetPassword(email: string, code: string, password: string): Promise<void> {
		const token = await this.verifyRecoveryCode(email, code);
		await this.confirmPasswordReset(token, password);
	}

	/**
	 * Verifica o código de recuperação de senha e retorna um token para alteração.
	 * @param email O email do usuário.
	 * @param code O código de 6 dígitos.
	 * @returns Retorna o token para redefinição de senha.
	 */
	static async verifyRecoveryCode(email: string, code: string): Promise<string> {
		const { token } = await APIClientServices.post<{ token: string }>("v1/auth/forgot-password", { email, code });
		return token;
	}

	/**
	 * Redefine a senha do usuário utilizando o token de recuperação.
	 * @param token O token de recuperação.
	 * @param password A nova senha.
	 */
	static async confirmPasswordReset(token: string, newPassword: string): Promise<void> {
		await APIClientServices.post("v1/auth/change-password", { token, newPassword });
	}

	/**
	 * Altera a senha do usuário logado.
	 * @param currentPassword A senha atual do usuário.
	 * @param newPassword A nova senha do usuário.
	 */
	static async changePassword(currentPassword: string, newPassword: string): Promise<void> {
		await APIClientServices.post("v1/auth/change-password", { currentPassword, newPassword });
	}

	static async enable2FA() {
		if (!this.currentUser) {
			throw new HandleError("User not found", "USER_NOT_FOUND", 404);
		}

		const response = await APIClientServices.post<AccountType.Enable2FAResponseType>("v1/auth/2fa/enable");
		this.currentUser = AccountModel.parse(this.currentUser.toJSON());
		return response;
	}

	static async verify2FA(code: string) {
		if (!this.currentUser) {
			throw new HandleError("User not found", "USER_NOT_FOUND", 404);
		}

		const response = await APIClientServices.post<{
			verified: boolean;
			error: string | null;
		}>("v1/auth/2fa/verify", { code });

		const user = this.currentUser.toJSON();
		user.twoFactorEnabled = response.verified;
		this.currentUser = AccountModel.parse(user);

		return response;
	}

	static async disable2FA(code: string) {
		if (!this.currentUser) {
			throw new HandleError("User not found", "USER_NOT_FOUND", 404);
		}

		await APIClientServices.post("v1/auth/2fa/disable", { code });

		const user = this.currentUser.toJSON();
		user.twoFactorEnabled = false;
		this.currentUser = AccountModel.parse(user);
	}

	// ─── PIN de Segurança ─────────────────────────────────────────────────────────

	static async enablePin(pin: string) {
		if (!this.currentUser) {
			throw new HandleError("User not found", "USER_NOT_FOUND", 404);
		}

		const response = await APIClientServices.post<{ enabled: boolean }>("v1/auth/pin/enable", { pin });

		const user = this.currentUser.toJSON();
		user.pinEnabled = true;
		this.currentUser = AccountModel.parse(user);

		return response;
	}

	static async verifyPin(pin: string) {
		if (!this.currentUser) {
			throw new HandleError("User not found", "USER_NOT_FOUND", 404);
		}

		return await APIClientServices.post<{ verified: boolean }>("v1/auth/pin/verify", { pin });
	}

	static async disablePin(pin: string) {
		if (!this.currentUser) {
			throw new HandleError("User not found", "USER_NOT_FOUND", 404);
		}

		await APIClientServices.post("v1/auth/pin/disable", { pin });

		const user = this.currentUser.toJSON();
		user.pinEnabled = false;
		this.currentUser = AccountModel.parse(user);
	}

	static async updatePinTimeout(minutes: number) {
		if (!this.currentUser) {
			throw new HandleError("User not found", "USER_NOT_FOUND", 404);
		}

		const response = await APIClientServices.put<{ timeoutMinutes: number }>("v1/auth/pin/timeout", { minutes });

		const user = this.currentUser.toJSON();
		user.pinTimeoutMinutes = response.timeoutMinutes;
		this.currentUser = AccountModel.parse(user);

		return response;
	}

	static async changePin(currentPin: string, newPin: string) {
		if (!this.currentUser) {
			throw new HandleError("User not found", "USER_NOT_FOUND", 404);
		}

		return await APIClientServices.post<{ changed: boolean }>("v1/auth/pin/change", { currentPin, newPin });
	}
}
